import type { StandingsRow } from '../types/standings.types'
import { RECORD_OUTCOMES } from './recordBarSegments'

/** A team's standings figures, as the teams sheet needs them. */
export interface TeamStandingsSummary {
  points: number
  /** `null` when the team has played no in-division games. */
  record: StandingsRow['record']
}

export type StandingsByTeam = Record<string, TeamStandingsSummary>

/**
 * Index standings rows by `teamId` so a grid row can look its own figures up.
 *
 * The teams sheet is built from the operations TREE, whose rows carry a
 * `teamId`, while standings arrive as a separate list keyed the same way. This
 * is the join between them. A team absent from the map has no standings entry
 * at all — which is not the same as a present entry with `record: null` (played
 * nothing), and callers distinguish the two.
 */
export const indexStandingsByTeam = (rows: StandingsRow[]): StandingsByTeam => {
  const map: StandingsByTeam = {}

  rows.forEach((row) => {
    if (!row?.teamId) {
      return
    }
    map[row.teamId] = { points: row.points, record: row.record }
  })

  return map
}

/**
 * The record as the chart prints it: all five outcomes joined, zeros included,
 * e.g. `"9-2-1-0-0"`. Returns `null` for a team that has played nothing, so the
 * caller picks the empty treatment rather than printing "0-0-0-0-0".
 */
export const formatRecordText = (
  record: StandingsRow['record']
): string | null =>
  record ? RECORD_OUTCOMES.map((outcome) => record[outcome]).join('-') : null
